// WeeklyHoursButton — top-bar clock for timesheet-creating users.
// Collapsed: a clock icon. Click → popover with this week's total hours and the
// per-day breakdown. Uses the per-user endpoint POST timesheet/myTimesheets/
// (same as the Timesheet Review page) with weekNumber:0 (current week); it returns
// a per-day array of { weekDay, hoursLogged } — no backend change.

import React, { useState, useCallback } from 'react';
import { baseUrl } from '../shared';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import NewTimesheetFlow from './timesheet/NewTimesheetFlow';

export default function WeeklyHoursButton() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState([]); // [{ weekDay, hoursLogged }]

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    fetch(baseUrl + 'timesheet/myTimesheets/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        username: localStorage.getItem('username'),
        bToken: localStorage.getItem('bToken'),
      },
      body: JSON.stringify({ weekNumber: 0 }),
    })
      .then((r) => r.json())
      .then((json) => {
        setDays(Array.isArray(json) ? json : []);
        setLoading(false);
      })
      .catch(() => { setError('Could not load hours.'); setLoading(false); });
  }, []);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    load();
  };

  const open = Boolean(anchorEl);
  const total = days.reduce((sum, d) => sum + (parseFloat(d.hoursLogged) || 0), 0);

  return (
    <NewTimesheetFlow>
      {(startNew) => (
    <>
      <Tooltip title="My hours this week">
        <IconButton onClick={handleOpen} sx={{ color: 'var(--on-rail)' }} aria-label="my hours this week">
          <AccessTimeIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 280, borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600 }}>
              My hours · this week
            </Typography>
            <Tooltip title="New timesheet">
              <IconButton
                size="small"
                aria-label="new timesheet"
                onClick={() => { setAnchorEl(null); startNew(); }}
                sx={{ color: 'var(--primary)', p: 0.25, ml: 1 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: 'var(--text)', mt: 0.25 }} className="aims-mono">
            {total.toFixed(2)}<Typography component="span" sx={{ fontSize: 13, color: 'var(--faint)', ml: 0.5 }}>h</Typography>
          </Typography>
        </Box>

        <Box sx={{ px: 1, py: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
          ) : error ? (
            <Typography variant="caption" sx={{ color: 'var(--fail)', px: 1 }}>{error}</Typography>
          ) : days.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'var(--muted)', px: 1, display: 'block', py: 1 }}>No hours logged this week.</Typography>
          ) : (
            days.map((d, i) => {
              const hrs = parseFloat(d.hoursLogged) || 0;
              return (
                <Box key={d.weekDay || i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5, opacity: hrs > 0 ? 1 : 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'var(--text)' }}>{d.weekDay}</Typography>
                  <Typography variant="body2" className="aims-mono" sx={{ fontWeight: hrs > 0 ? 600 : 400, color: hrs > 0 ? 'var(--text)' : 'var(--faint)' }}>
                    {hrs.toFixed(2)} h
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
      )}
    </NewTimesheetFlow>
  );
}
